import React, { useState } from "react";
import { Alert, Box, Chip, Grid, IconButton, MenuItem, Paper, Stack, TableCell, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import TableCommon from "../../components/Common/Table";
import { SearchRecords } from "../../components/Common/SearchRecords";
import ActionMenu, { ActionTypes } from "../../components/Common/ActionMenu";
import ShopOrderDetailsDialog from "../../components/admin/shop-orders/ShopOrderDetailsDialog";
import { STATUS_META } from "../../components/admin/shop-orders/shopOrderStatus";
import { useGetShopOrdersQuery } from "../../store/shopAdmin/shopAdminApis";
import { handleDateTimeDefault } from "../../helpers/utils";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const columns = [
    { title: "Order", field: "order_number", sort: false, width: "16%" },
    { title: "Customer", field: "customer", sort: false, width: "18%" },
    { title: "Items", field: "items", sort: false, width: "24%" },
    { title: "Total", field: "total_amount", sort: false, width: "10%", align: "right" },
    { title: "Status", field: "status", sort: false, width: "12%", align: "center" },
    { title: "Placed", field: "created_at", sort: true, width: "14%" },
    { title: "Actions", field: "actions", sort: false, width: "6%", align: "center" },
];

const ShopOrdersView = () => {
    const [pagination, setPagination] = useState({ pageNo: 0, limit: 10, sortBy: "DESC", sortField: "created_at", keywords: "" });
    const [status, setStatus] = useState("all");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [openOrderId, setOpenOrderId] = useState(null);

    const { data, isFetching, error, refetch } = useGetShopOrdersQuery({ ...pagination, status, from, to });
    const byStatus = data?.stats?.by_status || {};

    const handlePagination = ({ pageNo, limit, sortBy, sortField, keywords }) =>
        setPagination({ pageNo, limit, sortBy, sortField, keywords });

    const rows =
        !isFetching && data?.results?.length
            ? data.results.map((row) => {
                  const meta = STATUS_META[row.status] || { label: row.status, color: "default" };
                  return (
                      <TableRow
                          key={row.id}
                          hover
                          sx={{ "td, th": { border: 0, padding: "14px 12px" }, cursor: "pointer" }}
                          onClick={() => setOpenOrderId(row.id)}
                      >
                          <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                  {row.order_number}
                              </Typography>
                              {row.coupon_code ? <Chip size="small" variant="outlined" label={row.coupon_code} sx={{ mt: 0.5, height: 20 }} /> : null}
                              {row.referred ? <Chip size="small" color="info" variant="outlined" label="Referred" sx={{ mt: 0.5, ml: 0.5, height: 20 }} /> : null}
                          </TableCell>
                          <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                  {row.customer?.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                  {row.customer?.mobile}
                              </Typography>
                          </TableCell>
                          <TableCell>
                              <Typography variant="body2">{row.items_summary}</Typography>
                          </TableCell>
                          <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                  {rupees(row.total_amount)}
                              </Typography>
                          </TableCell>
                          <TableCell align="center">
                              <Chip size="small" label={meta.label} color={meta.color} />
                          </TableCell>
                          <TableCell>{handleDateTimeDefault(row.created_at)}</TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              <ActionMenu
                                  actions={[{ ...ActionTypes.VIEW, onClick: () => setOpenOrderId(row.id) }]}
                                  row={row}
                                  showStatusToggle={false}
                              />
                          </TableCell>
                      </TableRow>
                  );
              })
            : null;

    const cards = [
        { label: "Revenue (paid)", value: rupees(data?.stats?.revenue), color: "success.main" },
        { label: "To fulfil", value: (byStatus.paid || 0) + (byStatus.confirmed || 0) + (byStatus.packed || 0), color: "info.main" },
        { label: "Payment pending", value: byStatus.pending_payment || 0, color: "warning.main" },
        { label: "Delivered", value: byStatus.delivered || 0, color: "primary" },
    ];

    return (
        <Stack spacing={2}>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Typography variant="h6">
                        App Orders
                        <span style={{ color: "gray", fontSize: "10px", verticalAlign: "super", marginLeft: 8 }}>
                            ({data?.count || 0})
                        </span>
                    </Typography>
                </Grid>
                <Grid item>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPagination((p) => ({ ...p, pageNo: 0 })); }} sx={{ minWidth: 150 }}>
                            <MenuItem value="all">All</MenuItem>
                            {Object.entries(STATUS_META).map(([key, meta]) => (
                                <MenuItem key={key} value={key}>
                                    {meta.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
                        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
                        <Box sx={{ minWidth: 220 }}>
                            <SearchRecords handlePagination={handlePagination} pagination={pagination} />
                        </Box>
                        <Tooltip title="Refresh">
                            <IconButton size="small" onClick={refetch}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Grid>
            </Grid>

            <Alert severity="info">
                Orders customers placed in the MagicLock app (paid online, delivered through GoSwift). Search by order number, customer name or mobile. Field staff sales are in the Staff sales tab.
            </Alert>
            {error && <Alert severity="error">{error?.data?.message || "Failed to load shop orders"}</Alert>}

            <Grid container spacing={2}>
                {cards.map((c) => (
                    <Grid item xs={12} sm={6} md={3} key={c.label}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="h5" color={c.color}>
                                {c.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {c.label}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <TableCommon
                columns={columns}
                tableData={rows}
                count={data?.count || 0}
                loading={isFetching}
                pagination={pagination}
                handlePagination={handlePagination}
                emptyMessage="No app orders yet"
            />

            <ShopOrderDetailsDialog orderId={openOrderId} onClose={() => setOpenOrderId(null)} />
        </Stack>
    );
};

export default ShopOrdersView;
